import React, { useEffect, useMemo, useState } from "react";
import {
  fetchActivityInfo,
  fetchDrawResult,
  submitDraw,
} from "../services/luckyDrawApi";
import { ActivityInfo, Prize } from "../types/luckyDraw";
import "./LuckyDrawPage.css";

function normalizePhone(value: string): string {
  return value.replace(/\D/g, "");
}

function getStatusText(status?: string): string {
  switch (status) {
    case "not_started":
      return "活動尚未開始";
    case "active":
      return "活動進行中";
    case "ended":
      return "活動已結束";
    case "maintenance":
      return "系統維護中";
    default:
      return "狀態未知";
  }
}

const LuckyDrawPage: React.FC = () => {
  const [activity, setActivity] = useState<ActivityInfo | null>(null);
  const [phone, setPhone] = useState("");
  const [inputCode, setInputCode] = useState("");

  const [loadingActivity, setLoadingActivity] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [querying, setQuerying] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const [resultText, setResultText] = useState("");
  const [matchedCode, setMatchedCode] = useState("");
  const [matchedRulePositions, setMatchedRulePositions] = useState("");
  const [issuedCouponCode, setIssuedCouponCode] = useState("");
  const [prize, setPrize] = useState<Prize | null>(null);
  const [drawnAt, setDrawnAt] = useState("");

  const activityStatusText = useMemo(
    () => getStatusText(activity?.status),
    [activity]
  );

  useEffect(() => {
    const loadActivity = async () => {
      try {
        setLoadingActivity(true);
        setErrorMessage("");
        const data = await fetchActivityInfo();

        if (!data.success || !data.activity) {
          setErrorMessage(data.message || "無法取得活動資訊");
          return;
        }

        setActivity(data.activity);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "讀取活動資訊失敗"
        );
      } finally {
        setLoadingActivity(false);
      }
    };

    loadActivity();
  }, []);

  const clearResult = () => {
    setResultText("");
    setMatchedCode("");
    setMatchedRulePositions("");
    setIssuedCouponCode("");
    setPrize(null);
    setDrawnAt("");
  };

  const clearAll = () => {
    setPhone("");
    setInputCode("");
    setErrorMessage("");
    setInfoMessage("");
    clearResult();
  };

  const validatePhone = (): boolean => {
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setErrorMessage("請輸入手機號碼");
      return false;
    }
    if (normalized.length < 10) {
      setErrorMessage("手機號碼格式不正確");
      return false;
    }
    return true;
  };

  const handleQuery = async () => {
    if (!validatePhone()) return;

    try {
      setQuerying(true);
      setErrorMessage("");
      setInfoMessage("");
      clearResult();

      const normalized = normalizePhone(phone);
      const data: any = await fetchDrawResult(normalized);

      if (!data.success) {
        setErrorMessage(data.message || "查詢失敗");
        return;
      }

      if (!data.drawn) {
        setInfoMessage(data.message || "此手機號碼尚未抽獎");
        return;
      }

      setInfoMessage("已找到抽獎紀錄");
      setResultText(data.result_text || "");
      setMatchedCode(data.matched_code || "");
      setMatchedRulePositions(data.matched_rule_positions || "");
      setIssuedCouponCode(data.issued_coupon_code || "");
      setPrize(data.prize || null);
      setDrawnAt(data.created_at || "");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "查詢失敗");
    } finally {
      setQuerying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhone()) return;

    try {
      setSubmitting(true);
      setErrorMessage("");
      setInfoMessage("");
      clearResult();

      const normalized = normalizePhone(phone);

      const data: any = await submitDraw({
        phone: normalized,
        input_code: inputCode.trim(),
      });

      if (!data.success) {
        setErrorMessage(data.message || "抽獎失敗");
        return;
      }

      if (data.already_drawn) {
        setInfoMessage(data.message || "此手機號碼已抽過獎");
      } else {
        setInfoMessage(data.message || "抽獎完成");
      }

      setResultText(data.result_text || "");
      setMatchedCode(data.matched_code || "");
      setMatchedRulePositions(data.matched_rule_positions || "");
      setIssuedCouponCode(data.issued_coupon_code || "");
      setPrize(data.prize || null);
      setDrawnAt(data.created_at || "");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "抽獎失敗");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingActivity) {
    return (
      <div className="lucky-draw-loading">
        <div className="lucky-draw-loading-card">
          活動資訊載入中
          <span className="lucky-draw-dot-loader">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="lucky-draw-page">
      <div className="lucky-draw-card">
        <div className="lucky-draw-hero">
          <div className="lucky-draw-badge">🎁 幸運抽獎活動</div>

          {activity?.logo_url ? (
            <img
              src={activity.logo_url}
              alt="logo"
              className="lucky-draw-logo"
            />
          ) : null}

          <h1 className="lucky-draw-title">{activity?.title || "抽獎活動"}</h1>

          {activity?.subtitle ? (
            <p className="lucky-draw-subtitle">{activity.subtitle}</p>
          ) : null}

          <div className="lucky-draw-status-box">
            <strong>活動狀態：</strong>
            <span>{activityStatusText}</span>
          </div>

          {activity?.hero_image_url ? (
            <div className="lucky-draw-hero-image-wrap">
              <img
                src={activity.hero_image_url}
                alt="活動主圖"
                className="lucky-draw-hero-image"
              />
            </div>
          ) : null}
        </div>

        <div className="lucky-draw-body">
          <h2 className="lucky-draw-section-title">立即參加抽獎</h2>
          <p className="lucky-draw-section-desc">
            請輸入手機號碼，可搭配活動碼進行抽獎，系統也可查詢此號碼是否已抽過。
          </p>

          <div className="lucky-draw-form-panel">
            <form onSubmit={handleSubmit} className="lucky-draw-form">
              <div className="lucky-draw-grid">
                <div className="lucky-draw-field">
                  <label className="lucky-draw-label">手機號碼</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="例如 0912345678"
                    className="lucky-draw-input"
                  />
                </div>

                <div className="lucky-draw-field">
                  <label className="lucky-draw-label">獎項碼 / 活動碼</label>
                  <input
                    type="text"
                    value={inputCode}
                    onChange={(e) => setInputCode(e.target.value)}
                    placeholder="例如 A123（可不填）"
                    className="lucky-draw-input"
                  />
                </div>
              </div>

              <div className="lucky-draw-button-row">
                <button
                  type="submit"
                  disabled={submitting}
                  className="lucky-draw-primary-btn"
                >
                  {submitting
                    ? "送出中..."
                    : activity?.button_text || "立即查看結果"}
                </button>

                <button
                  type="button"
                  onClick={handleQuery}
                  disabled={querying}
                  className="lucky-draw-secondary-btn"
                >
                  {querying ? "查詢中..." : "查詢是否已抽過"}
                </button>

                <button
                  type="button"
                  onClick={clearAll}
                  className="lucky-draw-light-btn"
                >
                  清空重填
                </button>
              </div>
            </form>

            {errorMessage ? (
              <div className="lucky-draw-error">{errorMessage}</div>
            ) : null}

            {infoMessage ? (
              <div className="lucky-draw-info">{infoMessage}</div>
            ) : null}
          </div>

          {(resultText || prize || issuedCouponCode) && (
            <div className="lucky-draw-result-box">
              <h2 className="lucky-draw-result-title">抽獎結果</h2>

              {resultText ? (
                <p className="lucky-draw-result-text">{resultText}</p>
              ) : null}

              {prize ? (
                <div className="lucky-draw-prize-card">
                  <h3 className="lucky-draw-prize-name">{prize.name}</h3>

                  {prize.image_url ? (
                    <img
                      src={prize.image_url}
                      alt={prize.name}
                      className="lucky-draw-prize-image"
                    />
                  ) : null}

                  {prize.description ? (
                    <p className="lucky-draw-prize-description">
                      {prize.description}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="lucky-draw-meta-list">
                {matchedCode ? (
                  <p className="lucky-draw-meta-text">對應碼組：{matchedCode}</p>
                ) : null}

                {matchedRulePositions ? (
                  <p className="lucky-draw-meta-text">
                    規則碼位：{matchedRulePositions}
                  </p>
                ) : null}

                {issuedCouponCode ? (
                  <p className="lucky-draw-meta-text">
                    優惠碼：<strong>{issuedCouponCode}</strong>
                  </p>
                ) : null}

                {drawnAt ? (
                  <p className="lucky-draw-meta-text">
                    抽獎時間：{new Date(drawnAt).toLocaleString()}
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {activity?.announcement_html ? (
          <div className="lucky-draw-announcement-wrap">
            <h2 className="lucky-draw-announcement-title">活動說明</h2>
            <div
              className="lucky-draw-announcement"
              dangerouslySetInnerHTML={{ __html: activity.announcement_html }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LuckyDrawPage;